import { StateCreator } from "zustand";
import { ToWorkerMessage } from "Worker/entities";

export interface WebWorkerState {
  worker: Worker | null;
  /** A dropped file the fast route handed to the Upload panel; the panel consumes it on mount. */
  pendingUploadFile: File | null;
  initializeWorker: () => void;
  terminateWorker: () => void;
  /** `transfer` moves the listed buffers to the worker instead of copying
   *  them. A whole-file ArrayBuffer cloned rather than transferred doubles
   *  peak memory, which is what kills the tab on a large import. */
  sendMessageToWorker: (
    message: ToWorkerMessage,
    transfer?: Transferable[],
  ) => void;
  setPendingUploadFile: (file: File | null) => void;
}

/**
 * Stop recreating the worker after this many back-to-back failures.
 * Prevents an infinite crash → recreate → crash loop when the worker's
 * module graph is fundamentally broken (e.g. missing WASM). Keep this
 * small — each recreate is another zustand `set`, which re-renders every
 * subscriber of `state.worker`. A spree of rapid recreates can starve
 * React's update budget and surface as "Maximum update depth exceeded".
 */
const MAX_CONSECUTIVE_CRASHES = 2;

/**
 * Minimum delay between recreate attempts. When the worker's module graph
 * is broken, errors can fire synchronously on the same tick; without a
 * delay we'd burn through the crash budget in one microtask batch.
 */
const RECREATE_BACKOFF_MS = 500;

function createWorkerInstance(onError: (e: ErrorEvent) => void): Worker {
  const w = new Worker(new URL("../../../Worker/worker.ts", import.meta.url), {
    type: "module",
  });
  w.onerror = onError;
  return w;
}

function logWorkerError(e: ErrorEvent): void {
  // ErrorEvent fields are often empty for module-load failures; log every
  // field + the underlying Error (if any) so the real cause is visible.
  const details = {
    message: e.message || "(empty)",
    filename: e.filename || "(empty)",
    lineno: e.lineno,
    colno: e.colno,
    error: e.error, // the actual Error object, if the worker threw
  };
  console.error("Worker crashed:", details);
}

export const createWebWorkerState: StateCreator<WebWorkerState> = (
  set,
  get,
) => {
  let consecutiveCrashes = 0;

  const handleError = (e: ErrorEvent) => {
    logWorkerError(e);
    consecutiveCrashes += 1;
    if (consecutiveCrashes >= MAX_CONSECUTIVE_CRASHES) {
      console.error(
        `Worker crashed ${consecutiveCrashes} times in a row — giving up. ` +
          "Fix the worker module graph (check WASM builds and top-level imports).",
      );
      set({ worker: null });
      return;
    }
    // Reinitialize the worker after a small delay. Without the delay, workers
    // whose module graph crashes synchronously fire back-to-back error events
    // on the same tick, causing a burst of `set({worker})` calls that React
    // sees as a setState storm.
    setTimeout(() => {
      // If the store was terminated or another path replaced the worker in
      // the meantime, don't stomp on it.
      if (
        get().worker !== null &&
        consecutiveCrashes < MAX_CONSECUTIVE_CRASHES
      ) {
        const replacement = createWorkerInstance(handleError);
        set({ worker: replacement });
      }
    }, RECREATE_BACKOFF_MS);
  };

  // Reset the crash counter when the worker hasn't errored for a while —
  // transient failures shouldn't permanently disable the worker.
  const scheduleCrashCounterReset = () => {
    setTimeout(() => {
      if (get().worker) consecutiveCrashes = 0;
    }, 30_000);
  };

  return {
    worker: null,
    pendingUploadFile: null,

    setPendingUploadFile: (file) => set({ pendingUploadFile: file }),

    initializeWorker: () => {
      if (get().worker) return; // Prevent multiple workers
      consecutiveCrashes = 0;
      const newWorker = createWorkerInstance(handleError);
      set({ worker: newWorker });
      scheduleCrashCounterReset();
    },

    terminateWorker: () => {
      set((state) => {
        state.worker?.terminate();
        return { worker: null };
      });
    },

    sendMessageToWorker: (message, transfer) => {
      if (transfer && transfer.length > 0) {
        get().worker?.postMessage(message, transfer);
        return;
      }
      get().worker?.postMessage(message);
    },
  };
};
